import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import {
  BarChart3,
  Building2,
  Calendar,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Cpu,
  LayoutDashboard,
  LogOut,
  Mail,
  Phone,
  Search,
  UserPen,
  Settings,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { FeatureKey } from "@/api/types";
import {
  SIDEBAR_BG,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/stores/ui.store";
import { useAuthStore } from "@/stores/auth.store";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useFeature } from "@/hooks/useFeature";
import { useLogout } from "@/hooks/useLogout";
import { usePermissions, type Permissions } from "@/hooks/usePermissions";
import { initials } from "@/lib/formatters";
import { resolveAvatarColor } from "@/lib/avatarColor";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { IMPERSONATION_BANNER_HEIGHT } from "@/components/auth/ImpersonationBanner";
import { PrismMark } from "@/components/auth/PrismMark";
import { ProjectSelector } from "./ProjectSelector";

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  /** Predicate over the live permissions; the item is hidden when false. */
  visible: (p: Permissions) => boolean;
  /** Optional feature gate — when set, the item is also hidden if
   *  the project's `features_enabled[feature] === false`. */
  feature?: FeatureKey;
}

const MAIN_NAV: NavItem[] = [
  {
    label: "Dashboard",
    path: "dashboard",
    icon: LayoutDashboard,
    visible: (p) => p.canViewDashboard,
  },
  {
    label: "Analytics",
    path: "analytics",
    icon: BarChart3,
    visible: (p) => p.canViewAnalytics,
    feature: "analytics",
  },
  {
    label: "Companies",
    path: "companies",
    icon: Building2,
    // Always visible — viewers see qualified companies only via the API.
    visible: () => true,
  },
  {
    label: "Contacts",
    path: "contacts",
    icon: Users,
    visible: (p) => p.canViewContacts,
  },
  {
    label: "Enrichment",
    path: "enrichment",
    // Not a magnifier (Romaric, task #267): that one means search.
    icon: UserPen,
    // RBAC + feature flag. Superadmins toggle the feature in
    // Settings → Features when a project doesn't run an enrichment
    // workflow at all.
    visible: (p) => p.canAccessEnrichment,
    feature: "enrichment",
  },
  {
    label: "Email",
    path: "email",
    icon: Mail,
    // The current project's campaigns only: the permissions are this
    // project's role, and the backend refuses any other project's data.
    visible: (p) => p.canViewEmail,
  },
  {
    label: "Call Center",
    path: "call-center",
    icon: Phone,
    // RBAC + feature flag. Same gating shape as Enrichment.
    visible: (p) => p.canAccessCallCenter,
    feature: "call_center",
  },
  {
    label: "Tasks",
    path: "tasks",
    icon: CheckSquare,
    visible: (p) => p.canViewTasks,
  },
  {
    label: "Calendar",
    path: "calendar",
    icon: Calendar,
    visible: (p) => p.canViewCalendar,
    feature: "calendar",
  },
];

const ADMIN_NAV: NavItem[] = [
  {
    label: "Engine Control",
    path: "engine",
    icon: Cpu,
    visible: (p) => p.canViewEngine,
    feature: "engine",
  },
  {
    label: "Settings",
    path: "settings",
    icon: Settings,
    visible: (p) => p.canViewSettings,
  },
];

interface SidebarProps {
  /** Opens the global search (the Cmd+K palette). */
  onOpenSearch?: () => void;
}

export function Sidebar({ onOpenSearch }: SidebarProps = {}) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);
  const { project, roleName } = useCurrentProject();
  const perms = usePermissions();
  const navigate = useNavigate();
  const slug = useParams<{ slug?: string }>().slug ?? project?.slug ?? "";
  const avatar = resolveAvatarColor(user?.avatar_color, user?.id);
  // Logo click target — contextual: inside a project we go to that
  // project's dashboard; outside any project context (login, /projects
  // picker, /account without a slug) we fall back to the project list.
  const homeTarget = slug ? `/projects/${slug}/dashboard` : "/projects";
  // Shared with the header avatar dropdown — see useLogout.
  const handleLogout = useLogout();

  // Feature gates — every flag has to be read at the top level so
  // hook order stays stable. Items declare which feature they need
  // (if any) via NavItem.feature; when the flag is off the item is
  // hidden alongside its permission check.
  const featureFlags: Record<FeatureKey, boolean> = {
    scoring: useFeature("scoring"),
    lemlist: useFeature("lemlist"),
    engine: useFeature("engine"),
    analytics: useFeature("analytics"),
    calendar: useFeature("calendar"),
    tasks_kanban: useFeature("tasks_kanban"),
    call_center: useFeature("call_center"),
    enrichment: useFeature("enrichment"),
  };
  const isItemVisible = (item: NavItem) =>
    item.visible(perms) && (!item.feature || featureFlags[item.feature]);

  const visibleMain = MAIN_NAV.filter(isItemVisible);
  const visibleAdmin = ADMIN_NAV.filter(isItemVisible);

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  const impersonating = !!user?.impersonated_by;
  const topOffset = impersonating ? IMPERSONATION_BANNER_HEIGHT : 0;

  return (
    <aside
      className="fixed left-0 z-40 flex flex-col text-slate-300 shadow-2xl transition-[width] duration-200"
      style={{
        width,
        backgroundColor: SIDEBAR_BG,
        top: topOffset,
        height: `calc(100vh - ${topOffset}px)`,
      }}
    >
      <div
        className={cn(
          "flex items-center px-4 py-4",
          collapsed
            ? "flex-col gap-3"
            : "h-16 justify-between gap-3 py-0",
        )}
      >
        {/* The logo Link is a SIBLING of the chevron button below —
            never a parent — so the chevron click can't bubble through
            it. Layout / sizing classes match the previous decorative
            <div> verbatim; only behavior (Link + hover/focus) is new. */}
        <Link
          to={homeTarget}
          aria-label="Go to home"
          className={cn(
            "flex items-center gap-3 rounded-lg transition-colors hover:bg-white/5",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20",
            collapsed
              ? "h-10 w-10 justify-center"
              : "min-w-0 px-1",
          )}
        >
          <PrismMark
            size={collapsed ? 28 : 24}
            className="shrink-0 text-white transition-[width,height] duration-150"
          />
          <span
            className={cn(
              "whitespace-nowrap text-xl font-semibold tracking-tight text-white transition-opacity duration-150",
              collapsed ? "hidden" : "opacity-100",
            )}
          >
            Prism
          </span>
        </Link>
        <button
          onClick={toggleSidebar}
          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      <div className="px-3 pb-2">
        <ProjectSelector collapsed={collapsed} />
      </div>

      {onOpenSearch && (
        <div className="px-3">
          <SidebarSearch collapsed={collapsed} onOpen={onOpenSearch} />
        </div>
      )}

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {visibleMain.map((item) => (
          <SidebarLink
            key={item.path}
            slug={slug}
            item={item}
            collapsed={collapsed}
          />
        ))}
        {visibleAdmin.length > 0 && (
          <>
            <div className="px-3 pb-1 pt-4">
              {!collapsed && (
                <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
                  Admin
                </div>
              )}
              {collapsed && <div className="mx-2 h-px bg-white/5" />}
            </div>
            {visibleAdmin.map((item) => (
              <SidebarLink
                key={item.path}
                slug={slug}
                item={item}
                collapsed={collapsed}
              />
            ))}
          </>
        )}
      </nav>

      <div
        className="border-t p-2"
        style={{ borderColor: "#1a1a24" }}
      >
        <TooltipProvider delayDuration={150}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(slug ? `/projects/${slug}/account` : "/projects")
                    }
                    aria-label="Account settings"
                    className="rounded-full transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                  >
                    <Avatar className="h-7 w-7 border border-white/10">
                      <AvatarFallback
                        className={cn(
                          "text-[10px] font-semibold",
                          avatar.bg,
                          avatar.text,
                        )}
                      >
                        {initials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {user?.full_name ?? "Account"}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => void handleLogout()}
                    aria-label="Sign out"
                    className="rounded-md p-1 text-slate-400 transition-colors hover:bg-white/5 hover:text-rose-400"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Sign out</TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <div className="flex items-center">
              <button
                type="button"
                onClick={() =>
                  navigate(slug ? `/projects/${slug}/account` : "/projects")
                }
                className="group flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                aria-label="Account settings"
              >
                <Avatar className="h-8 w-8 shrink-0 border border-white/10">
                  <AvatarFallback
                    className={cn(
                      "text-xs font-semibold",
                      avatar.bg,
                      avatar.text,
                    )}
                  >
                    {initials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">
                    {user?.full_name ?? "Unknown"}
                  </div>
                  <div className="truncate text-[11px] text-slate-400">
                    {user?.email ?? roleName ?? "—"}
                  </div>
                </div>
              </button>
              <span
                aria-hidden
                className="mx-1 h-7 w-px shrink-0"
                style={{ backgroundColor: "#2a2a34" }}
              />
              <div className="flex shrink-0 items-center gap-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => void handleLogout()}
                      aria-label="Sign out"
                      className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/5 hover:text-rose-400"
                    >
                      <LogOut className="h-[18px] w-[18px]" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Sign out</TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
        </TooltipProvider>
      </div>
    </aside>
  );
}

/** The global search, at the top of the sidebar under the project (Romaric,
 *  Prism task #268, 2026-10-26: "Basculer la barre de recherche tout en
 *  haut pour pouvoir la voir de manière intuitive"; it sat at the header's
 *  right). The magnifier now means search only: Enrichment has its own
 *  icon since task #267. */
function SidebarSearch({
  collapsed,
  onOpen,
}: {
  collapsed: boolean;
  onOpen: () => void;
}) {
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label="Search (Cmd+K)"
        title="Search"
        className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
      >
        <Search className="h-4 w-4" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Search (Cmd+K)"
      className={cn(
        "flex w-full items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-400 transition-colors",
        "hover:bg-white/10 hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/40",
      )}
    >
      <Search className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-left">Search…</span>
      <kbd className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
        {isMac ? "⌘K" : "Ctrl K"}
      </kbd>
    </button>
  );
}

interface SidebarLinkProps {
  slug: string;
  item: NavItem;
  collapsed: boolean;
}

function SidebarLink({ slug, item, collapsed }: SidebarLinkProps) {
  const Icon = item.icon;
  const to = `/projects/${slug}/${item.path}`;
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
          collapsed && "justify-center",
          isActive
            ? "bg-white/10 text-white"
            : "text-slate-400 hover:bg-white/5 hover:text-slate-100",
        )
      }
      title={collapsed ? item.label : undefined}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}
